import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CustomsQueueRow } from '@crm/shared'
import {
  addComment,
  deleteComment,
  getComments,
  updateComment,
} from '../../../shared/api/comments.api'
import { getCustomsItem, getCustomsMentionableUsers } from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import type { Comment } from '../../../shared/api/types'
import { toast } from '../../../shared/ui/toastStore'
import { confirm, useConfirmStore } from '../../../shared/ui/confirmStore'
import { Spinner } from '../../../shared/ui/Spinner'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { CustomsStatusBadge } from '../../../shared/ui/Badge'
import { useAuthStore } from '../../auth/authStore'
import { CommentItem } from '../../requests/components/CommentItem'
import { MentionInput, type MentionInputHandle } from '../../requests/components/MentionInput'
import { ReplyHintBar } from '../../requests/components/ReplyHintBar'
import {
  AttachImageButton,
  DraftImages,
  canSendComment,
  useCommentImages,
} from '../../requests/components/commentImages'

interface Props {
  itemId: string
  /** Строка очереди, если панель открыта из грида: шапка видна сразу, без ожидания запроса. */
  row?: CustomsQueueRow | null
  onClose: () => void
}

/**
 * Комментарии к позиции прямо в очереди нетарифки: логист карточку заявки не
 * открывает (решение 24.09), поэтому переписка с менеджером — выдвижной панелью
 * справа. Ветка та же, что в карточке заявки у менеджера и закупки.
 */
export function CustomsCommentsDrawer({ itemId, row, onClose }: Props) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [text, setText] = useState('')
  const [mentions, setMentions] = useState<string[]>([])
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const inputRef = useRef<MentionInputHandle>(null)
  const images = useCommentImages()
  const commentsKey = ['comments', 'item', itemId]

  const itemQuery = useQuery({
    queryKey: ['customs', 'item', itemId],
    queryFn: () => getCustomsItem(itemId),
    placeholderData: row ?? undefined,
    staleTime: 0,
  })
  const item = itemQuery.data

  const commentsQuery = useQuery({
    queryKey: commentsKey,
    queryFn: () => getComments({ requestItemId: itemId }),
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
  const comments = commentsQuery.data ?? []

  // Неотправленный текст не теряем молча: Escape, клик по затемнению и крестик
  // сначала спрашивают. Текст читаем из ref — обработчик Escape висит один.
  const textRef = useRef(text)
  textRef.current = text
  const requestClose = useCallback(async () => {
    if (textRef.current.trim()) {
      // Снять фокус с поля ввода: иначе Enter в окне «Закрыть?» сначала поймает
      // поле комментария и отправит его менеджеру.
      ;(document.activeElement as HTMLElement | null)?.blur()
      const ok = await confirm({
        title: 'Закрыть?',
        message: 'Неотправленный комментарий пропадёт.',
        confirmText: 'Закрыть',
      })
      if (!ok) return
    }
    onClose()
  }, [onClose])

  // Escape закрывает панель, если его не забрали раньше: список @-упоминаний и
  // правка комментария гасят его сами (preventDefault), окно подтверждения
  // («Удалить комментарий?», «Закрыть?») закрывается своим обработчиком —
  // панель при этом остаётся. Слушаем document: наш обработчик повешен раньше,
  // чем у окна подтверждения.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (useConfirmStore.getState().current) return
      void requestClose()
    }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [requestClose])

  const refreshAfterChange = () => {
    void qc.invalidateQueries({ queryKey: commentsKey })
    // Счётчик «💬» в очереди.
    void qc.invalidateQueries({ queryKey: ['customs', 'queue'] })
  }

  const addMut = useMutation({
    mutationFn: () =>
      addComment({
        text,
        requestItemId: itemId,
        mentions,
        parentId: replyTo?.id ?? null,
        imageIds: images.ids,
      }),
    onSuccess: () => {
      setText('')
      images.clear()
      setMentions([])
      setReplyTo(null)
      refreshAfterChange()
    },
    onError: (e) => toast.error(apiErrorText(e, 'Не удалось отправить комментарий')),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, nextText }: { id: string; nextText: string }) =>
      updateComment(id, { text: nextText }),
    onSuccess: refreshAfterChange,
    onError: (e) => toast.error(apiErrorText(e, 'Не удалось изменить комментарий')),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: (_d, id) => {
      if (replyTo?.id === id) setReplyTo(null)
      refreshAfterChange()
    },
    onError: (e) => toast.error(apiErrorText(e, 'Не удалось удалить комментарий')),
  })

  const handleReply = useCallback((c: Comment) => {
    setReplyTo(c)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  const handleParentClick = useCallback((parentId: string) => {
    const el = document.querySelector<HTMLElement>(`[data-focus-id="${parentId}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.classList.add('ring-2', 'ring-brand-500')
    setTimeout(() => el.classList.remove('ring-2', 'ring-brand-500'), 1500)
  }, [])

  const canSend = canSendComment(text, images) && !addMut.isPending
  const submit = () => {
    if (canSend) addMut.mutate()
  }

  const loadUsers = useCallback(() => getCustomsMentionableUsers(itemId), [itemId])

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        data-testid="customs-comments-backdrop"
        onClick={() => void requestClose()}
      />
      <aside
        role="dialog"
        aria-label="Комментарии к позиции"
        className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] bg-white border-l border-gray-200 shadow-xl flex flex-col"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            <h3 className="text-sm font-semibold text-gray-900 font-mono truncate">
              {item?.partNumber ?? 'Позиция'}
            </h3>
            {item && (
              <>
                <p className="text-xs text-gray-500 truncate">
                  Заявка {item.requestNumber ?? '—'}
                  {item.clientCode && <span className="font-mono"> · {item.clientCode}</span>}
                  {' · '}
                  {item.brand || 'производитель не указан'}
                  {' · '}
                  {item.quantity.toLocaleString('ru-RU')} шт.
                </p>
                <p className="text-xs text-gray-500 truncate">
                  Менеджер: {item.managerName ?? '—'}
                </p>
                <div className="pt-1">
                  <CustomsStatusBadge status={item.status} />
                </div>
              </>
            )}
          </div>
          <button
            onClick={() => void requestClose()}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {itemQuery.isError && !item ? (
            <ErrorMessage error={itemQuery.error} />
          ) : commentsQuery.isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : commentsQuery.isError ? (
            <ErrorMessage error={commentsQuery.error} />
          ) : comments.length === 0 ? (
            <p className="text-xs text-gray-400">
              {user?.role === 'LOGIST'
                ? 'Комментариев пока нет. Напишите менеджеру — он получит уведомление.'
                : 'Комментариев пока нет.'}
            </p>
          ) : (
            comments.map((c) => (
              <CommentItem
                key={c.id}
                comment={c}
                currentUserId={user?.id}
                onReply={handleReply}
                onSubmitEdit={async (id, nextText) => {
                  await updateMut.mutateAsync({ id, nextText })
                }}
                onDelete={async (id) => {
                  await deleteMut.mutateAsync(id)
                }}
                onParentClick={handleParentClick}
                topBadge={
                  c.quoteId ? (
                    <div className="mb-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                      К квоте
                    </div>
                  ) : null
                }
              />
            ))
          )}
        </div>

        <div className="border-t border-gray-100 p-3 space-y-1">
          {replyTo && <ReplyHintBar parent={replyTo} onClear={() => setReplyTo(null)} />}
          <DraftImages state={images} />
          <div className="flex items-end gap-2">
            <MentionInput
              ref={inputRef}
              value={text}
              onChange={(t, m) => {
                setText(t)
                setMentions(m)
              }}
              onSubmit={submit}
              onPasteFiles={images.addFiles}
              allowEmptySubmit={canSend}
              requestId={undefined}
              itemId={itemId}
              loadUsers={loadUsers}
              placeholder="Комментарий… (@ — упомянуть; Enter — отправить)"
              disabled={addMut.isPending}
            />
            <AttachImageButton state={images} disabled={addMut.isPending} />
            <button
              onClick={submit}
              disabled={!canSend}
              aria-label="Отправить комментарий"
              className="px-3 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
            >
              →
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
